import { useState } from 'react'

import { USERS_ACCESS_ROLE_LABEL_KEYS } from '../../constants/usersAccess'
import { useUsersAccessMutations } from '../../hooks/useUsersAccessMutations'
import type {
  UsersAccessRole,
  UsersAccessUserRowI,
} from '../../types/usersAccess'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Modal, Stack, Text, errorColorFor } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import type { Role, RoleExtended } from '../../types/types'
import { RoleCheckboxOption } from './RoleCheckboxOption'

const CHANGEABLE_ROLES: UsersAccessRole[] = [
  'IMPLEMENTER',
  'PROGRAM_MANAGER',
  'OPERATOR',
  'GUARD',
  'READ_ONLY_USER',
  'EMPLOYEE',
]

interface ChangeUserRolesModalIProps {
  colorScheme: ColorScheme
  isOpen: boolean
  onClose: () => void
  row: UsersAccessUserRowI
}

export const ChangeUserRolesModal = ({
  colorScheme,
  isOpen,
  onClose,
  row,
}: ChangeUserRolesModalIProps) => {
  const { t } = useTranslation()
  const { hasCRUDPermission } = usePermissions()
  const { changeRoles, isUpdatingRoles } = useUsersAccessMutations(colorScheme)

  // ENG-33697: roles the viewer may not grant (Implementer, for PMs) stay
  // visible but disabled; toggleRole also guards so a restricted role can
  // never enter the selection even programmatically
  const isRoleRestricted = (role: UsersAccessRole) =>
    !hasCRUDPermission(role as Role | RoleExtended)
  const [selectedRoles, setSelectedRoles] = useState<UsersAccessRole[]>(
    row.roles
  )
  const [hasError, setHasError] = useState(false)

  const toggleRole = (role: UsersAccessRole) => {
    if (isRoleRestricted(role)) return
    setHasError(false)
    setSelectedRoles((current) => {
      if (current.includes(role)) {
        return current.filter((value) => value !== role)
      }
      return role === 'EMPLOYEE'
        ? ['EMPLOYEE']
        : [...current.filter((value) => value !== 'EMPLOYEE'), role]
    })
  }

  const save = async () => {
    if (selectedRoles.length === 0) {
      setHasError(true)
      return
    }
    const didSave = await changeRoles(
      row,
      selectedRoles.filter((role) => role !== 'EMPLOYEE')
    )
    if (didSave) onClose()
  }

  return (
    <Modal
      closeOnEsc={!isUpdatingRoles}
      closeOnOverlayClick={!isUpdatingRoles}
      colorScheme={colorScheme}
      header={<Text textStyle='caption'>{row.fullName || row.email}</Text>}
      isCloseHidden={isUpdatingRoles}
      isOpen={isOpen}
      isSaveLoading={isUpdatingRoles}
      maxW='480px'
      onClose={onClose}
      onSave={save}
      saveButtonLabel={t('settings:rebrand_users_change_roles_submit')}
      showCancelButton={!isUpdatingRoles}
      size='compact'
      title={t('settings:rebrand_users_change_roles_title')}
    >
      <Stack gap='8px'>
        {CHANGEABLE_ROLES.map((role) => (
          <RoleCheckboxOption
            colorScheme={colorScheme}
            isChecked={selectedRoles.includes(role)}
            isRestricted={isRoleRestricted(role)}
            key={role}
            label={t(USERS_ACCESS_ROLE_LABEL_KEYS[role])}
            onChange={() => toggleRole(role)}
            restrictedLabel={t('settings:rebrand_users_role_restricted_hint')}
            role={role}
            testIdPrefix='ChangeUserRolesModal'
          />
        ))}
        {hasError && (
          <Text
            color={errorColorFor(colorScheme)}
            role='alert'
            textStyle='caption'
          >
            {t('settings:rebrand_users_roles_required')}
          </Text>
        )}
      </Stack>
    </Modal>
  )
}
